import { useState } from "react";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useDrillDownDialog } from "../DrillDownProvider";
import { PivotNode, PivotResult } from "../pivot";
import { DrillDownConfig, WidgetExplanationContent } from "../types";
import { WidgetCard } from "./WidgetCard";
import { dashboardTableSx, ROW_HOVER_BACKGROUND, ZEBRA_ROW_BACKGROUND } from "./tableStyles";

type PivotTableCardProps<ItemType> = {
    title: string;
    explanation: WidgetExplanationContent;
    pivot: PivotResult<ItemType>;
    rowHeaderLabel: string;
    maxBodyHeight?: number;
    headerControls?: React.ReactNode;
    isLoading?: boolean;
    isError?: boolean;
    cellDrillDown?: (node: PivotNode<ItemType>, columnKey: string) => DrillDownConfig | null;
    // Predicado leve (sem construir o drill) que decide se uma célula é clicável — permite tornar só
    // certas colunas drilláveis (ex.: só "Encomendas"). Sem ele, todas as células com valor são clicáveis.
    cellIsClickable?: (node: PivotNode<ItemType>, columnKey: string) => boolean;
    // Marca opcional ao lado do valor (ex.: seta de desvio ao mês típico). Devolver null/undefined deixa
    // a célula EXATAMENTE como sem a prop — é isso que evita envolver milhares de células sem necessidade.
    cellAdornment?: (node: PivotNode<ItemType>, columnKey: string, isTotalsRow: boolean) => React.ReactNode;
    renderNodeLabel?: (node: PivotNode<ItemType>) => React.ReactNode;
};

const pathKey = (node: PivotNode<unknown>) => node.path.join(" › ");

export const PivotTableCard = <ItemType,>({
    title,
    explanation,
    pivot,
    rowHeaderLabel,
    maxBodyHeight,
    headerControls,
    isLoading,
    isError,
    cellDrillDown,
    cellIsClickable,
    cellAdornment,
    renderNodeLabel,
}: PivotTableCardProps<ItemType>) => {
    const [expandedPaths, setExpandedPaths] = useState<Array<string>>([]);
    const drillDownDialog = useDrillDownDialog();

    const toggleNode = (node: PivotNode<ItemType>) => {
        const key = pathKey(node);
        setExpandedPaths((current) => (current.includes(key) ? current.filter((path) => path !== key) : [...current, key]));
    };

    const openCellDrillDown = (node: PivotNode<ItemType>, columnKey: string) => {
        const config = cellDrillDown ? cellDrillDown(node, columnKey) : null;
        if (config && drillDownDialog) drillDownDialog.openDrillDown(config);
    };

    const renderValueCells = (node: PivotNode<ItemType>, isTotalsRow: boolean) =>
        pivot.columns.map((column) => {
            const isClickable =
                Boolean(cellDrillDown && drillDownDialog) &&
                !isTotalsRow &&
                node.formattedByColumn[column.key] !== "" &&
                node.items.length > 0 &&
                (cellIsClickable ? cellIsClickable(node, column.key) : true);
            // Formatação por célula (ex.: semáforo). Quando pinta um fundo, o hover clicável passa a
            // brightness (o pseudo-class backgroundColor ganharia sempre à cor do semáforo — CSS specificity).
            const cellSxResult = column.cellSx ? column.cellSx(node.rawByColumn[column.key], node, isTotalsRow) : undefined;
            const hasCellBackground = Boolean(cellSxResult && "backgroundColor" in (cellSxResult as Record<string, unknown>));
            const hoverSx = hasCellBackground ? { "&:hover": { filter: "brightness(0.95)" } } : { "&:hover": { backgroundColor: ROW_HOVER_BACKGROUND } };
            const adornment = cellAdornment ? cellAdornment(node, column.key, isTotalsRow) : null;
            return (
                <TableCell
                    key={column.key}
                    align={column.isNumeric ? "right" : "left"}
                    onClick={isClickable ? () => openCellDrillDown(node, column.key) : undefined}
                    sx={{
                        ...(isTotalsRow ? { fontWeight: 700, borderTop: "2px solid #d6d9de" } : {}),
                        ...(isClickable ? { cursor: "pointer", ...hoverSx } : {}),
                        ...(cellSxResult || {}),
                    }}
                >
                    {adornment ? (
                        <Box display="flex" alignItems="center" gap={0.5} justifyContent={column.isNumeric ? "flex-end" : "flex-start"}>
                            {adornment}
                            {node.formattedByColumn[column.key]}
                        </Box>
                    ) : (
                        node.formattedByColumn[column.key]
                    )}
                </TableCell>
            );
        });

    const renderNodeRows = (nodes: Array<PivotNode<ItemType>>): Array<React.ReactNode> =>
        nodes.flatMap((node) => {
            const key = pathKey(node);
            const isExpanded = expandedPaths.includes(key);
            const hasChildren = node.children.length > 0;
            const nodeRow = (
                <TableRow key={key}>
                    <TableCell sx={{ paddingLeft: 1 + node.depth * 3, fontWeight: node.depth === 0 ? 600 : 400 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {hasChildren ? (
                                <IconButton size="small" onClick={() => toggleNode(node)} sx={{ padding: 0.25 }}>
                                    {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                                </IconButton>
                            ) : (
                                <Box width={24} />
                            )}
                            {renderNodeLabel ? renderNodeLabel(node) : node.label}
                        </Box>
                    </TableCell>
                    {renderValueCells(node, false)}
                </TableRow>
            );
            const childRows = hasChildren && isExpanded ? renderNodeRows(node.children) : [];
            return [nodeRow, ...childRows];
        });

    return (
        <WidgetCard title={title} explanation={explanation} bodyHeight={160} isLoading={isLoading} isError={isError} headerControls={headerControls}>
            <Box width="100%">
                <TableContainer sx={{ maxHeight: maxBodyHeight }}>
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            ...dashboardTableSx,
                            "& tbody tr:nth-of-type(odd):not(:last-child)": { backgroundColor: ZEBRA_ROW_BACKGROUND },
                            // Realce da linha sob o cursor (exclui a linha de totais). O :nth-of-type(n)
                            // eleva a especificidade acima da regra da zebra para ganhar em qualquer linha.
                            "& tbody tr:nth-of-type(n):not(:last-child):hover": { backgroundColor: ROW_HOVER_BACKGROUND },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>{rowHeaderLabel}</TableCell>
                                {pivot.columns.map((column) => (
                                    <TableCell key={column.key} align={column.isNumeric ? "right" : "left"} sx={{ fontWeight: 600 }}>
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderNodeRows(pivot.rows)}
                            <TableRow key="__totals">
                                <TableCell sx={{ fontWeight: 700, borderTop: "2px solid #d6d9de" }}>{pivot.totals.label}</TableCell>
                                {renderValueCells(pivot.totals, true)}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </WidgetCard>
    );
};
